import { action, computed } from 'mobx';
import {
    LoanApplicationBackofficeAPIApiFindAllLoanApplicationsForBackofficeRequest,
    LoanApplicationDto,
    TransactionDto,
} from 'src/generated-api-client';
import { applicationsApi } from 'src/services/apiServices';
import {
    AsyncFunction,
    AsyncOperationWithStatus,
} from 'src/utils/mobx/AsyncOperationWithStatus';
import { BasicStore } from 'src/utils/mobx/BasicStore/BasicStore';
import { BasicStoreApi } from 'src/utils/mobx/BasicStore/BasicStore.types';
import { FilterCriteria } from 'src/utils/mobx/FilterCriteria';
import { Pager } from 'src/utils/mobx/Pager';
import { Sorter } from 'src/utils/mobx/Sorter';
import { RequestHelper } from 'src/utils/RequestHelper';

export class ApplicationsStoreClass extends BasicStore<
    LoanApplicationDto,
    Record<string, any>,
    LoanApplicationDto,
    LoanApplicationDto,
    LoanApplicationDto
> {
    api: BasicStoreApi<
        LoanApplicationDto,
        LoanApplicationDto,
        LoanApplicationDto,
        LoanApplicationDto
    > = {
        loadList: async (...args) => {
            const options = RequestHelper.getOptionsFromArgs(args);

            const response = await RequestHelper.unwrapFromAxiosPromise(
                applicationsApi.findAllLoanApplicationsForBorrower(
                    {
                        ...this.filterCriteria.filter,
                        page: this.pager?.page,
                        size: this.pager?.size,
                        sort: this.sorter?.sort,
                    },
                    options,
                ),
            );
            this.pager?.setTotal(response.totalElements ?? 0);

            return response.content ?? [];
        },
        loadItem: async (id, ...args) => {
            const options = RequestHelper.getOptionsFromArgs(args);

            const response = await RequestHelper.unwrapFromAxiosPromise(
                applicationsApi.getLoanApplicationForBorrower({ id }, options),
            );

            return response ?? null;
        },
    };

    transactionsLoader = new AsyncOperationWithStatus<
        AsyncFunction,
        Array<TransactionDto>
    >(
        (id: string, ...args) => {
            const options = RequestHelper.getOptionsFromArgs(args);

            return RequestHelper.unwrapFromAxiosPromise(
                applicationsApi.getTransactionsForBorrower({ id }, options),
            );
        },
        { defaultIsLoading: true },
    );

    @action
    loadTransactions(id: string) {
        return this.transactionsLoader.call(id);
    }

    @computed
    get transactions() {
        return this.transactionsLoader.data || [];
    }

    pager = new Pager();
    sorter = new Sorter<LoanApplicationDto>();
    filterCriteria =
        new FilterCriteria<LoanApplicationBackofficeAPIApiFindAllLoanApplicationsForBackofficeRequest>();
}

export const ApplicationsStore = new ApplicationsStoreClass();
